import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BRAND } from "@/lib/api";

/**
 * LogoWatermarks
 *
 * Scatters 3-6 subtle, animated brand-mark watermarks into every <section>
 * on the current page (excluding navbar + footer which have their own logos).
 *
 * Behaviour:
 *  - Skips sections smaller than ~340px tall.
 *  - 3-6 watermarks per section (based on a stable per-section seed).
 *  - Sizes: desktop 45-70px (max 80px), mobile 28-42px (responsive via JS).
 *  - Opacity: 0.10 – 0.18 light bg / 0.08 – 0.14 dark bg.
 *  - 5 animations (A floatY, B softPulse, C slowSpin, D gentleDrift, E scaleBreath).
 *  - z-index 0 layer with pointer-events: none — never blocks clicks.
 */

const ANIMATIONS = ["wm-anim-A", "wm-anim-B", "wm-anim-D"];

// Position presets — corners, edges, and a handful of mid-side spots.
// Some are intentionally cropped at edges via negative offsets.
const POSITIONS = [
    { top: "6%",     left: "3%"   },
    { top: "8%",     right: "4%"  },
    { top: "32%",    left: "-1%"  },  // partially cropped left
    { top: "48%",    right: "1%"  },
    { bottom: "10%", left: "5%"   },
    { bottom: "6%",  right: "8%"  },
    { top: "62%",    left: "8%"   },
    { bottom: "22%", right: "-2%" },  // partially cropped right
    { top: "18%",    right: "18%" },
    { bottom: "30%", left: "20%"  },
];

function hashCode(str) {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
    return Math.abs(h);
}

function isMobile() {
    return window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
}

// Density-aware count based on section height.
// Large (>900px): 3-5 marks · Medium (500-900): 2-3 · Small (340-500): 1-2.
function pickCount(seed, heightPx) {
    if (heightPx >= 900) return 3 + (seed % 3); // 3,4,5
    if (heightPx >= 500) return 2 + (seed % 2); // 2,3
    return 1 + (seed % 2);                       // 1,2
}

function build(seed, isDark, heightPx) {
    const count = pickCount(seed, heightPx);
    const mobile = isMobile();
    const items = [];
    // Shuffled positions per seed so different sections look different.
    const order = POSITIONS.map((_, i) => i).sort(
        (a, b) => ((seed * (a + 1)) % 100) - ((seed * (b + 1)) % 100)
    );
    for (let i = 0; i < count; i++) {
        const s = seed + i * 37;
        // Desktop 48-72px (max 80) · Mobile 30-45px
        const sizeBase = mobile ? 30 + (s % 16) : 48 + (s % 25);
        const opacityRange = isDark ? [0.06, 0.10] : [0.08, 0.14];
        const opacity =
            opacityRange[0] + (((s * 7) % 100) / 100) * (opacityRange[1] - opacityRange[0]);
        const animClass = ANIMATIONS[(s + i) % ANIMATIONS.length];
        const pos = POSITIONS[order[i % order.length]];
        const rotate = ((s * 11) % 22) - 11; // -11..+11deg
        items.push({ pos, sizePx: sizeBase, opacity, animClass, rotate });
    }
    return { items, isDark };
}

function decorateSections() {
    // Skip anything inside the footer or navbar
    const skipAncestor = (el) => el.closest("footer, nav, [data-wm-skip]");
    const sections = document.querySelectorAll(
        "section:not([data-wm-injected])"
    );
    sections.forEach((sec, idx) => {
        if (skipAncestor(sec)) {
            sec.setAttribute("data-wm-injected", "true");
            return;
        }
        sec.setAttribute("data-wm-injected", "true");

        const style = window.getComputedStyle(sec);
        if (style.position === "static") sec.style.position = "relative";

        const heightPx = sec.offsetHeight || 0;
        if (heightPx < 340) {
            // Too small for any watermark — skip entirely.
            sec.setAttribute("data-wm-skip", "true");
            return;
        }

        const bg = style.backgroundColor || "";
        const explicit = sec.getAttribute("data-watermark-tone");
        const looksDark =
            explicit === "dark" ||
            sec.classList.contains("gradient-navy") ||
            /rgb\((1[0-3]|[0-9])\s*,\s*([0-3][0-9]|[0-9])\s*,\s*([0-5][0-9]|[0-9])\)/.test(bg);

        const layer = document.createElement("div");
        layer.className = "wm-layer";
        layer.setAttribute("aria-hidden", "true");

        const seed = hashCode(sec.id || sec.className || `sec-${idx}`);
        const { items, isDark } = build(seed, looksDark, heightPx);
        if (items.length === 0) return;
        for (const it of items) {
            const img = document.createElement("img");
            img.src = isDark ? BRAND.logoWhite : BRAND.logo;
            img.alt = "";
            img.draggable = false;
            img.className = `wm-mark ${it.animClass}`;
            img.style.height = `${it.sizePx}px`;
            img.style.width = "auto";
            img.style.opacity = it.opacity.toFixed(3);
            // Inline rotate baked into transform so spin/breath animations layer cleanly
            img.style.transform = `rotate(${it.rotate}deg)`;
            for (const [k, v] of Object.entries(it.pos)) img.style[k] = v;
            layer.appendChild(img);
        }

        if (!sec.querySelector(":scope > [data-wm-content]")) {
            const wrap = document.createElement("div");
            wrap.setAttribute("data-wm-content", "true");
            wrap.style.position = "relative";
            wrap.style.zIndex = "1";
            while (sec.firstChild) wrap.appendChild(sec.firstChild);
            sec.appendChild(layer);
            sec.appendChild(wrap);
        } else {
            sec.insertBefore(layer, sec.firstChild);
        }
    });
}

export default function LogoWatermarks() {
    const { pathname } = useLocation();
    useEffect(() => {
        const t1 = setTimeout(decorateSections, 80);
        const t2 = setTimeout(decorateSections, 400);
        const t3 = setTimeout(decorateSections, 1200);
        const obs = new MutationObserver(() => decorateSections());
        obs.observe(document.body, { childList: true, subtree: true });
        const tStop = setTimeout(() => obs.disconnect(), 3500);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            clearTimeout(tStop);
            obs.disconnect();
        };
    }, [pathname]);
    return null;
}
